import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  ArrowUpToLine,
  BookText,
  Building2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  CircleMinus,
  CirclePlus,
  CloudUpload,
  Copy,
  Download,
  Edit,
  Ellipsis,
  Eye,
  FileImage,
  FileText,
  FileType,
  Filter,
  Funnel,
  HelpCircle,
  House,
  Image,
  Info,
  Key,
  LayoutDashboard,
  Link,
  ListCheck,
  LoaderCircle,
  Mail,
  Maximize2,
  Menu,
  Minimize2,
  Minus,
  OctagonX,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  Power,
  RotateCcw,
  Search,
  SendHorizontal,
  Settings,
  Sheet,
  Trash2,
  Trash2Icon,
  TriangleAlert,
  Upload,
  User,
  X,
} from "lucide-react";
import { cn } from "../lib/utils";

export type IconSymbol =
  | "alert"
  | "arrow-left"
  | "arrow-right"
  | "book-text"
  | "building-2"
  | "check"
  | "chevron-down"
  | "chevron-left"
  | "chevron-right"
  | "chevron-up"
  | "circle-alert"
  | "circle-check"
  | "circle-minus"
  | "circle-plus"
  | "cloud-upload"
  | "copy"
  | "download"
  | "edit"
  | "ellipsis"
  | "eye"
  | "file-image"
  | "file-text"
  | "file-type"
  | "filter"
  | "funnel"
  | "help"
  | "house"
  | "image"
  | "info"
  | "key"
  | "layout-dashboard"
  | "link"
  | "list-check"
  | "loader-circle"
  | "mail"
  | "maximize-2"
  | "menu"
  | "minimize-2"
  | "minus"
  | "octagon-x"
  | "panel-left-close"
  | "panel-left-open"
  | "pencil"
  | "plus"
  | "power"
  | "publish"
  | "rotate-ccw"
  | "search"
  | "send-horizontal"
  | "settings"
  | "sheet"
  | "trash"
  | "trash-2"
  | "triangle-alert"
  | "upload"
  | "user"
  | "x";

// Map your string names to lucide components
const ICONS: Record<
  IconSymbol,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  alert: AlertCircle,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "book-text": BookText,
  "building-2": Building2,
  check: Check,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevron-up": ChevronUp,
  "circle-alert": CircleAlert,
  "circle-check": CircleCheck,
  "circle-plus": CirclePlus,
  "cloud-upload": CloudUpload,
  copy: Copy,
  download: Download,
  "circle-minus": CircleMinus,
  edit: Edit,
  ellipsis: Ellipsis,
  eye: Eye,
  "file-image": FileImage,
  "file-text": FileText,
  "file-type": FileType,
  filter: Filter,
  funnel: Funnel,
  help: HelpCircle,
  house: House,
  image: Image,
  info: Info,
  key: Key,
  "layout-dashboard": LayoutDashboard,
  link: Link,
  "list-check": ListCheck,
  "loader-circle": LoaderCircle,
  mail: Mail,
  "maximize-2": Maximize2,
  menu: Menu,
  "minimize-2": Minimize2,
  minus: Minus,
  "octagon-x": OctagonX,
  "panel-left-close": PanelLeftClose,
  "panel-left-open": PanelLeftOpen,
  pencil: Pencil,
  plus: Plus,
  power: Power,
  publish: ArrowUpToLine,
  "rotate-ccw": RotateCcw,
  search: Search,
  "send-horizontal": SendHorizontal,
  settings: Settings,
  sheet: Sheet,
  trash: Trash2,
  "trash-2": Trash2Icon,
  "triangle-alert": TriangleAlert,
  upload: Upload,
  user: User,
  x: X,
};

export interface IconProps {
  icon: IconSymbol;
  className?: string;
  size?: number; // in px
  color?: string; // stroke color
  strokeWidth?: number; // default 2
  onClick?: () => void;
  disabled?: boolean;
}

export const Icon: React.FC<IconProps> = ({
  icon,
  className = "",
  size = 20,
  color,
  strokeWidth = 2,
  onClick,
  disabled = false,
}) => {
  const LucideIcon = ICONS[icon];

  if (!LucideIcon) {
    // Optional: render a fallback or nothing if icon not found
    return null;
  }

  const iconElement = (
    <LucideIcon
      color={color}
      strokeWidth={strokeWidth}
      onClick={disabled ? undefined : onClick}
      className={cn(
        disabled &&
          "text-gray-500 hover:text-gray-500 cursor-not-allowed pointer-events-none",
        className,
      )}
      width={size}
      height={size}
    />
  );

  return iconElement;
};

export default Icon;
